import React, { DragEvent, ReactElement, useCallback } from 'react'

import { TreeItem } from '../../../TreeItem'

export interface TreeNode {
  id: string
  name: string
  parentId: string | null
  childIds: string[]
}

interface TreeNodeItemProps {
  depth?: number
  expandedIds: Set<string>
  id: string
  nodes: Record<string, TreeNode>
  onNodeDragOver?: (id: string, event: DragEvent) => void
  onNodeDrop?: (id: string, event: DragEvent) => void
  onSelect: (id: string) => void
  onToggle: (id: string) => void
  selectedId?: string
}

export const TreeNodeItem = React.memo(
  ({
    depth = 0,
    expandedIds,
    id,
    nodes,
    onNodeDragOver,
    onNodeDrop,
    onSelect,
    onToggle,
    selectedId,
  }: TreeNodeItemProps): ReactElement | null => {
    const node = nodes[id]

    const handleToggleClick = useCallback(() => onToggle(id), [id, onToggle])
    const handleItemClick = useCallback(() => {
      onSelect(id)
      if (node?.childIds.length > 0) {
        onToggle(id)
      }
    }, [id, node?.childIds.length, onSelect, onToggle])
    const handleDragOver = useCallback((event: DragEvent) => onNodeDragOver?.(id, event), [id, onNodeDragOver])
    const handleDrop = useCallback((event: DragEvent) => onNodeDrop?.(id, event), [id, onNodeDrop])

    if (!node) {
      return null
    }

    return (
      <TreeItem
        depth={depth}
        isExpanded={expandedIds.has(id)}
        isSelected={selectedId === id}
        label={node.name}
        onClick={handleItemClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onToggle={handleToggleClick}
      >
        {node.childIds.length > 0 && (
          <ul>
            {node.childIds.map((childId) => (
              <TreeNodeItem
                key={childId}
                depth={depth + 1}
                expandedIds={expandedIds}
                id={childId}
                nodes={nodes}
                onNodeDragOver={onNodeDragOver}
                onNodeDrop={onNodeDrop}
                onSelect={onSelect}
                onToggle={onToggle}
                selectedId={selectedId}
              />
            ))}
          </ul>
        )}
      </TreeItem>
    )
  },
)
